import autobind from 'autobind-decorator';
import ky, { type Options } from 'ky';
import { merge } from 'lodash';
import { getTokenProperty } from './utility';
import { createTempSessionId } from './requests/createTempSessionId';

export class HttpClient {
    private kyInstance: typeof ky;

    public meetingId: string | number | null = null;
    public userUuid: string | null = null;
    public token: string | null = null;

    private defaultMiddleware = [this.userUuidMiddleware] as const;

    constructor(
        public apiUrl: string,
        options?: Parameters<typeof HttpClient.prototype.setupOptions>[0]
    ) {
        this.setupOptions(options);

        this.kyInstance = ky.create({
            prefixUrl: apiUrl,
            retry: {
                limit: 2,
                backoffLimit: 1000,
                methods: ['get', 'post', 'put', 'delete'],
                statusCodes: [408, 413, 429, 500, 502, 503, 504, 521, 522, 524]
            },
            timeout: 30000,
            hooks: {
                beforeRetry: [
                    ({ request, error, retryCount }) => {
                        console.warn(`Retrying request to ${request.url} for the ${retryCount} time. Error: ${error}`);
                    }
                ]
            }
        });
    }

    @autobind
    public authMiddleware(options: Options): Options {
        if (!this.token) {
            throw new Error('This request requires an authentication token. Update HttpClient.token first.');
        }

        return {
            ...options,
            headers: {
                ...options.headers,
                Token: this.token,
                'X-Class-Token': this.token
            }
        };
    }

    @autobind
    public meetingPrefixMiddleware(options: Options): Options {
        if (!this.meetingId && this.token) {
            this.meetingId = getTokenProperty(this.token, 'meeting_id');
        }

        if (!this.meetingId) {
            throw new Error('This request requires a meeting id. Update HttpClient.meetingId first.');
        }

        return {
            ...options,
            prefixUrl: new URL(`meeting/${this.meetingId}`, this.apiUrl).toString()
        };
    }

    @autobind
    public userUuidMiddleware(options: Options): Options {
        if (!this.userUuid && !this.token) {
            return options;
        }

        if (!this.userUuid && this.token) {
            const userUuid = getTokenProperty(this.token, 'user_uuid');
            if (!userUuid) {
                return options;
            }

            this.userUuid = userUuid;
        }

        if (!this.userUuid) {
            return options;
        }

        return {
            ...options,
            headers: {
                ...options.headers,
                User_uuid: this.userUuid,
                'X-Class-User-UUID': this.userUuid
            }
        };
    }

    public use(...middleware: Array<(options: Options) => Options>) {
        let defaultOptions: Options = {};

        for (const handler of [...this.defaultMiddleware, ...middleware]) {
            defaultOptions = merge({}, defaultOptions, handler(defaultOptions));
        }

        return this.kyInstance.extend(defaultOptions);
    }

    public async createTempSessionId(singleUse = true) {
        if (!this.userUuid && !this.token) {
            throw new Error(
                'This request requires a userUuid or a valid access token. Update HttpClient.userUuid first.'
            );
        }

        const tempSessionId = await createTempSessionId(this, { singleUse });
        return tempSessionId.tempSessionId;
    }

    public default() {
        return this.kyInstance;
    }

    private setupOptions(options?: {
        meetingId?: typeof HttpClient.prototype.meetingId;
        userUuid?: typeof HttpClient.prototype.userUuid;
        token?: typeof HttpClient.prototype.token;
    }) {
        this.meetingId = options?.meetingId ?? this.meetingId;
        this.userUuid = options?.userUuid ?? this.userUuid;
        this.token = options?.token ?? this.token;
    }
}
